import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { 
  Star, Plus, LogOut, Settings, ExternalLink, 
  Chrome, Facebook, MapPin, BarChart3, MessageSquare,
  Instagram, Linkedin, Twitter, Youtube, Music,
  ChevronDown, MousePointer, TrendingUp, Home, BedDouble
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  chrome: Chrome,
  facebook: Facebook,
  'map-pin': MapPin,
  star: Star,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  music: Music,
  home: Home,
  'bed-double': BedDouble,
};

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "https://www.reviewmybusiness.ie";

const DashboardPage = () => {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [plaques, setPlaques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPlaques: 0,
    ratingPlaques: 0,
    followPlaques: 0,
    totalClicks: 0
  });

  useEffect(() => {
    fetchPlaques();
  }, []);

  const fetchPlaques = async () => {
    try {
      const response = await axios.get(`${API}/plaques`);
      setPlaques(response.data);
      
      const ratingPlaques = response.data.filter(p => p.platform_type === 'rating').length;
      const followPlaques = response.data.filter(p => p.platform_type === 'follow').length;
      const totalClicks = response.data.reduce((sum, p) => sum + (p.click_count || 0), 0);
      
      setStats({
        totalPlaques: response.data.length,
        ratingPlaques,
        followPlaques,
        totalClicks
      });
    } catch (error) {
      toast.error('Failed to load plaques');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = WEBSITE_URL;
    toast.success('Logged out successfully');
  };

  const getPlatformIcon = (iconName) => {
    const Icon = platformIcons[iconName] || Star;
    return Icon;
  };

  const ratingPlaques = plaques.filter(p => p.platform_type === 'rating');
  const followPlaques = plaques.filter(p => p.platform_type === 'follow');

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href={WEBSITE_URL} className="flex items-center gap-2">
              <img src="/logo.png" alt="ReviewMyBusiness.ie" className="h-10" />
            </a>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-[var(--color-text-secondary)] hidden sm:inline">
              {user?.business_name}
            </span>
            <Link to="/settings">
              <Button variant="ghost" size="sm" data-testid="settings-link">
                <Settings className="w-4 h-4" />
              </Button>
            </Link>
            {user?.is_admin && !isImpersonating && (
              <Link to="/admin">
                <Button variant="outline" size="sm" data-testid="admin-link-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-dashboard-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Welcome, {user?.business_name}
            </h1>
            <p className="text-[var(--color-text-secondary)] mt-1">
              Manage your rating and follow plaques
            </p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-full btn-lift" data-testid="register-plaque-dropdown">
                  <Plus className="w-4 h-4 mr-2" />
                  Register Plaque
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/register-plaque" className="flex items-center" data-testid="register-rating-link">
                    <Star className="w-4 h-4 mr-2" />
                    Rating Plaque
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/register-follow" className="flex items-center" data-testid="register-follow-link">
                    <Instagram className="w-4 h-4 mr-2" />
                    Follow Plaque
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Link to="/analytics">
            <Button variant="outline" size="sm" data-testid="analytics-link">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </Button>
          </Link>
          <Link to="/feedback">
            <Button variant="outline" size="sm" data-testid="feedback-link">
              <MessageSquare className="w-4 h-4 mr-2" />
              Manage Feedback
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Total Plaques
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="total-plaques">
                    {stats.totalPlaques}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[var(--color-accent)]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Rating Plaques
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="rating-plaques">
                    {stats.ratingPlaques}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Follow Plaques
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="follow-plaques">
                    {stats.followPlaques}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Follow Clicks
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="total-clicks">
                    {stats.totalClicks}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <MousePointer className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rating Plaques */}
        <Card className="border-0 shadow-sm mb-6">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="font-dashboard-heading text-lg font-semibold flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Rating Plaques
              </CardTitle>
              <Link to="/register-plaque">
                <Button variant="ghost" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : ratingPlaques.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-dashboard-heading font-semibold text-lg mb-2">No rating plaques yet</h3>
                <p className="text-[var(--color-text-muted)] mb-4">
                  Register your first rating plaque to start collecting reviews
                </p>
                <Link to="/register-plaque">
                  <Button className="rounded-full" data-testid="empty-register-rating-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Register Rating Plaque
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Platform</th>
                      <th>Business Name</th>
                      <th>Threshold</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratingPlaques.map((plaque) => {
                      const PlatformIcon = getPlatformIcon(plaque.platform_icon);
                      return (
                        <tr key={plaque.id} data-testid={`plaque-row-${plaque.code}`}>
                          <td>
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                              {plaque.code}
                            </code>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <PlatformIcon className="w-4 h-4 text-[var(--color-text-secondary)]" />
                              <span>{plaque.platform_name}</span>
                            </div>
                          </td>
                          <td>{plaque.business_name || '—'}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              {plaque.threshold_stars}
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            </div>
                          </td>
                          <td>
                            <Badge className={plaque.is_registered ? 'badge-active' : 'badge-inactive'}>
                              {plaque.is_registered ? 'Active' : 'Pending'}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Link to={`/plaque/${plaque.id}`}>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  data-testid={`view-plaque-${plaque.code}`}
                                >
                                  View
                                </Button>
                              </Link>
                              {plaque.is_registered && (
                                <a 
                                  href={`/r/${plaque.code}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  <Button variant="ghost" size="sm">
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Follow Plaques */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="font-dashboard-heading text-lg font-semibold flex items-center gap-2">
                <Instagram className="w-5 h-5 text-purple-500" />
                Follow Plaques
              </CardTitle>
              <Link to="/register-follow">
                <Button variant="ghost" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : followPlaques.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Instagram className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-dashboard-heading font-semibold text-lg mb-2">No follow plaques yet</h3>
                <p className="text-[var(--color-text-muted)] mb-4">
                  Register follow plaques to grow your social media following
                </p>
                <Link to="/register-follow">
                  <Button className="rounded-full" data-testid="empty-register-follow-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Register Follow Plaque
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Platform</th>
                      <th>Business Name</th>
                      <th>Clicks</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followPlaques.map((plaque) => {
                      const PlatformIcon = getPlatformIcon(plaque.platform_icon);
                      return (
                        <tr key={plaque.id} data-testid={`follow-row-${plaque.code}`}>
                          <td>
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                              {plaque.code}
                            </code>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <PlatformIcon className="w-4 h-4 text-[var(--color-text-secondary)]" />
                              <span>{plaque.platform_name}</span>
                            </div>
                          </td>
                          <td>{plaque.business_name || '—'}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <MousePointer className="w-3 h-3 text-green-500" />
                              {plaque.click_count || 0}
                            </div>
                          </td>
                          <td>
                            <Badge className={plaque.is_registered ? 'badge-active' : 'badge-inactive'}>
                              {plaque.is_registered ? 'Active' : 'Pending'}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Link to={`/plaque/${plaque.id}`}>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  data-testid={`view-follow-${plaque.code}`}
                                >
                                  View
                                </Button>
                              </Link>
                              {plaque.is_registered && (
                                <a 
                                  href={`/f/${plaque.code}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  <Button variant="ghost" size="sm">
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardPage;
